import makeErrorFromUnknown from '@/functions/makeErrorFromUnknown';
import { BaseError } from '@/modules/errors/constants/error-class-map.gen';
import {
  type ILogger,
  type LoggerNamespace,
  type LogLevel,
  knownLoggerNamespaces,
} from '@/modules/logger/interfaces/logger';
import captureException from '@/modules/sentry/functions/captureException';
import { isNil } from 'lodash-es';
import loglevel from 'loglevel';
import { match, P } from 'ts-pattern';
import { singleton } from 'tsyringe';

/**
 * The default logger for the TireCloud
 * which is a wrapper around the "loglevel" logger.
 * @see {@link https://github.com/pimterry/loglevel}
 * @see {@link TireCloudCoreLogger} for the logger interface.
 */
@singleton()
export default class Logger implements ILogger {
  /**
   * Configured log level
   */
  private level: LogLevel;

  /**
   * Configured namespace padding
   */
  private namespacePadding: number;

  /**
   * Create a new logger instance
   */
  constructor() {
    this.level = 'trace';
    this.namespacePadding = Math.max(
      ...knownLoggerNamespaces.map((ns) => ns.length)
    );
    this.applyLogLevelPlugin();
  }

  /**
   * Apply custom loglevel plugin
   */
  private applyLogLevelPlugin() {
    const originalFactory = loglevel.methodFactory;
    loglevel.methodFactory = (methodName, level, logger) => {
      return (message, ...args) => {
        const name = `${logger.toString()}:`;
        const lvl = methodName.toUpperCase().padStart(5);
        const ns = name.padStart(this.namespacePadding);
        const fmt = `[${new Date().toISOString()}] ${lvl} ${ns} ${message}`;
        if (methodName === 'debug') {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, no-console
          console.debug(fmt, ...args);
        } else {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          originalFactory(methodName, level, logger)(fmt, ...args);
        }
      };
    };
    loglevel.rebuild();
  }

  /**
   * Create a new logger instance
   * @param namespace - The namespace to create the logger for
   * @returns The logger instance
   */
  private createLogger(namespace: LoggerNamespace): loglevel.Logger {
    const logger = loglevel.getLogger(namespace);
    logger.setLevel(this.level);
    return logger;
  }

  /**
   * Get a logger instance for a given namespace
   * @param namespace - The namespace to get the logger for
   * @returns The logger instance
   */
  private getNsLogger(namespace: LoggerNamespace): loglevel.Logger {
    return this.createLogger(namespace);
  }

  /**
   * Log a message with the trace level
   * @param namespace - The namespace to log the message from
   * @param rest - The rest of the arguments to log
   */
  trace(namespace: LoggerNamespace, ...rest: any[]): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.getNsLogger(namespace).trace(...rest);
  }

  /**
   * Log a message with the debug level
   * @param namespace - The namespace to log the message from
   * @param rest - The rest of the arguments to log
   */
  debug(namespace: LoggerNamespace, ...rest: any[]): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.getNsLogger(namespace).debug(...rest);
  }

  /**
   * Log a message with the info level
   * @param namespace - The namespace to log the message from
   * @param rest - The rest of the arguments to log
   */
  info(namespace: LoggerNamespace, ...rest: any[]): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.getNsLogger(namespace).info(...rest);
  }

  /**
   * Log a message with the warn level
   * @param namespace - The namespace to log the message from
   * @param rest - The rest of the arguments to log
   */
  warn(namespace: LoggerNamespace, ...rest: any[]): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    this.getNsLogger(namespace).warn(...rest);
  }

  /**
   * Log a message with the error level
   * @param namespace - The namespace to log the message from
   * @param error - The error to log
   * @param data - The additional data to log
   * @param data.message - The message to log
   * @param data.tags - The tags to log
   * @param data.extras - The extra data to log
   * @param data.displayErrorName - Whether to display the error name
   * @param sendErrorLog - Whether to report the error to the server
   */
  async error(
    namespace: LoggerNamespace,
    error: unknown = new Error(),
    data?: {
      message?: string;
      tags?: Record<string, string>;
      extras?: Record<string, unknown>;
      displayErrorName?: string;
    },
    sendErrorLog = true
  ) {
    const [mainNs, ...subNs] = namespace.split('.');
    const nsLogger = this.getNsLogger(mainNs);

    const module = mainNs;
    const submodule =
      Array.isArray(subNs) && subNs.length > 0 ? subNs.join('.') : undefined;
    const commonDataToLog = { module, submodule, ...data };

    const errorObj = makeErrorFromUnknown(error);

    const errorObjectToLog = match(errorObj)
      .with(P.instanceOf(BaseError), (e) => e.originalError ?? e)
      .otherwise(() => errorObj);

    // if there's a display name to override the error name, use it
    if (!isNil(data?.displayErrorName)) {
      // @ts-expect-error - We are adding a new property to the error object
      errorObjectToLog.originalErrorName = errorObjectToLog.name;
      errorObjectToLog.name = data.displayErrorName;
    }
    // if the error is a BaseError, use the parent (base error) name as the error name
    else if (errorObj instanceof BaseError) {
      // @ts-expect-error - We are adding a new property to the error object
      errorObjectToLog.originalErrorName = errorObj.constructor.name;
      errorObjectToLog.name = errorObj.constructor.name;
    }

    const originalErrorMessage = errorObjectToLog.message;
    // if there's a message to override the error message, use it
    if (!isNil(data?.message)) {
      // @ts-expect-error - We are adding a new property to the error object
      errorObjectToLog.originalErrorMessage = errorObjectToLog.message;
      errorObjectToLog.message = data.message;
    }
    // if the error is a BaseError, use the original constructor name and message as the error message
    else if (errorObj instanceof BaseError) {
      // @ts-expect-error - We are adding a new property to the error object
      errorObjectToLog.originalErrorMessage = errorObjectToLog.message;
      errorObjectToLog.message = `${errorObjectToLog.constructor.name}: ${originalErrorMessage}`;
    }

    nsLogger.error(errorObjectToLog.toString(), '\n', {
      error: errorObjectToLog,
      originalError: errorObj,
      ...commonDataToLog,
    });
    nsLogger.error('Stack Trace:', errorObjectToLog.stack);
    nsLogger.error('Original Stack Trace:', errorObj.stack);
    if (sendErrorLog) captureException(errorObjectToLog, commonDataToLog);
  }
}
